import lang from "../core/lang";

class Questionnaire {
    #config = {}
    #title = undefined
    #sections = undefined

    constructor(config) {
        this.#config = config;
    }

    numOfSections() {
        return this.sections.length;
    }

    get title() {
        if(this.#title===undefined) {
            const {title = null} = this.#config;
            this.#title = title;
        }
        return this.#title;
    }

    get sections() {
        if(this.#sections===undefined) {
            const {sections = []} = this.#config;
            this.#sections = sections.map(section => Section.of(section));
        }

        return this.#sections
    }

    getSection(index) {
        let section;
        if(lang.isNumber(index)) {
            section = this.sections[index];
        } else {
            section = this.sections.find(section => section.subject===index);
        }
        return section;
    }

    setSection(index, section) {
        let effectiveIndex = Math.min(index, this.numOfSections());
        if(!lang.isNumber(index)) {
            effectiveIndex = this.sections.findIndex(section => section.subject===index) || this.numOfSections();
        }

        this.sections[effectiveIndex] = section;
    }

    isCompleted() {
        return this.sections.every(section => section.isCompleted());
    }

    find(predicate) {
        let found;
        for(const section of this.sections) {
            const questions = section.getQuestions(true);
            for(const question of questions) {
                if(predicate(question)) {
                    found = question;
                    break;
                }
            }
            if(found) {
                break;
            }
        }
        return found;
    }

    toJSON() {
        return ({
            title: this.title,
            sections: this.sections
        });
    }

    static fromJSON(json) {
        if(lang.isNullOrUndefined(json)) {
            return json;
        } else {
            return new Questionnaire(json);
        }
    }

    clone() {
        const json = this.toJSON();
        return Questionnaire.fromJSON(json);
    }

    static of(value) {
        let questionnaire;
        if(lang.isNullOrUndefined(value)) {
            questionnaire = value;
        } else if(value instanceof Questionnaire) {
            questionnaire = value.clone();
        } else if(lang.isObject(value)) {
            questionnaire = new Questionnaire(value);
        } else {
            throw new Error(`Type is not supported. Supported types are Questionnaire or JSON object, but receive: ${value}`);
        }
        return questionnaire;
    }
}

class Section {
    #config = {}
    #subject = undefined
    #description = undefined
    #questions = undefined

    constructor(config) {
        this.#config = config;
    }

    numOfQuestions() {
        return this.questions?.length || 0;
    }

    get subject() {
        if(this.#subject===undefined) {
            const {subject = null} = this.#config;
            this.#subject = subject;
        }
        return this.#subject;
    }

    get description() {
        if(this.#description===undefined) {
            const {description = null} = this.#config;
            this.#description = description;
        }
        return this.#description;
    }

    get questions() {
        if(this.#questions===undefined) {
            const {questions = []} = this.#config;
            this.#questions = questions.map(question => Question.of(question));
        }
        return this.#questions;
    }

    getQuestions(flatten) {
       if(flatten) {
           const fn = (question) => {
               const questions = question.questions.reduce((questions, question) => {
                   return [...questions, ...fn(question)];
               }, [question]);
               return questions;
           }
           return this.questions.reduce((questions, question) => {
               return [...questions, ...fn(question)];
           }, []);
       } else {
           return this.questions;
       }
    }

    getQuestion(index, flatten) {
        const questions = this.getQuestions(flatten);
        let question;
        if(lang.isNumber(index)) {
            question = questions[index];
        } else {
            question = questions.find(question => question.description===index);
        }
        return question;
    }

    setQuestion(index, question, flatten) {
        const questions = this.getQuestions(flatten);
        let effectiveIndex;
        if(lang.isNumber(index)) {
            effectiveIndex = Math.min(index, questions.length);
        } else {
            effectiveIndex = questions.findIndex(question => question.description===index) || questions.length;
        }
        questions[effectiveIndex].answer = question.answer;
    }

    isCompleted() {
        return this.questions.every(question => question.isCompleted());
    }

    toJSON() {
        return ({
            subject: this.subject,
            description: this.description,
            questions: this.questions
        });
    }

    static fromJSON(json) {
        if(lang.isNullOrUndefined(json)) {
            return json;
        } else {
            return new Section(json);
        }
    }

    clone() {
        const json = this.toJSON();
        return Section.fromJSON(json);
    }

    static of(value) {
        let section;
        if(lang.isNullOrUndefined(value)) {
            section = value;
        } else if(value instanceof Section) {
            section = value.clone();
        } else if(lang.isObject(value)) {
            section = new Section(value);
        } else {
            throw new Error(`Type is not supported. Supported types are Section or JSON object, but receive: ${value}`);
        }
        return section;
    }
}

class Question {
    #config = {}
    #type = undefined
    #required = undefined
    #disabled = undefined
    #condition = undefined
    #description = undefined
    #options = undefined
    #answer = undefined
    #questions = undefined
    #format = undefined
    #helperText = undefined

    constructor(config) {
        this.#config = config;
    }

    get type() {
        if(this.#type===undefined) {
            const {type = null} = this.#config;
            this.#type = type;
        }
        return this.#type;
    }

    get required() {
        if(this.#required===undefined) {
            const {required = true} = this.#config;
            this.#required = required;
        }
        return this.#required;
    }

    get condition() {
        if(this.#condition===undefined) {
            const {condition = null} = this.#config;
            this.#condition = condition;
        }
        return this.#condition;
    }

    get disabled() {
        if(this.#disabled===undefined) {
            const {disabled = false} = this.#config;
            this.#disabled = disabled;
        }
        return this.#disabled;
    }

    get description() {
        if(this.#description===undefined) {
            const {description = null} = this.#config;
            this.#description = description;
        }
        return this.#description;
    }

    get helperText() {
        if(this.#helperText===undefined) {
            const {helperText = null} = this.#config;
            this.#helperText = helperText;
        }
        return this.#helperText;
    }

    get options() {
        if(this.#options===undefined) {
            let {options = []} = this.#config;
            if(lang.isEmpty(options) && this.type===Question.Type.YNQ) {
                options = [true, false]
            }
            this.#options = options.map(option => Option.of(option));
        }
        return this.#options;
    }

    get answer() {
        if(this.#answer===undefined) {
            const {answer = null} = this.#config;
            this.#answer = Answer.of(answer);
        }
        return this.#answer;
    }

    set answer(answer) {
        this.#answer = answer;
        this.validate();
    }

    get questions() {
        if(this.#questions===undefined) {
            const {questions = []} = this.#config;
            this.#questions = questions.map(question => Question.of(question));
            this.validate();
        }
        return this.#questions;
    }

    get format() {
        if(this.#format===undefined) {
            const {format = null} = this.#config;
            this.#format = Format.of(format);
        }
        return (this.#format?.fields.length > 0) ? this.#format : null;
    }

    validate() {
        for(const question of this.questions) {
            let disabled = true;
            const values = lang.isArray(this.answer?.value) ? this.answer?.value : [this.answer?.value];
            for(const value of values) {
                if(question.test(value)) {
                    disabled = false;
                    break;
                }
            }
            question.disable(disabled);
            if(disabled) {
                question.answer = null;
            }
        }
    }

    test(value) {
        let enabled;
        if(lang.isNotEmpty(this.condition)) {
            if(lang.isNotEmpty(value)) {
                if (lang.isString(this.condition)) {
                    const regexp = new RegExp(`^${this.condition}$`);
                    enabled = regexp.test(value);
                } else {
                    enabled = this.condition===value;
                }
            } else {
                enabled = false;
            }
        } else {
            enabled = true;
        }
        return enabled;
    }

    disable(disabled = true) {
        this.#disabled = disabled;
    }

    isRequired() {
        return this.required;
    }

    isCompleted() {
        let completed;

        if(this.isAnswered()) {
            const values = lang.isArray(this.answer.value) ? this.answer.value : [this.answer.value];
            completed = this.questions.filter(question => values.some(value => question.test(value))).every(question => question.isCompleted());
        } else {
            completed = !this.isRequired();
        }

        return completed;
    }

    isAnswered() {
        let answered;

        if(lang.isEmpty(this.answer?.value)) {
            answered = false;
        } else {
            const values = lang.isArray(this.answer.value) ? this.answer.value : [this.answer.value];
            if(this.format?.fields?.length > 0) {
                // 答案有格式要求，例如 oeq
                const fields = this.format.fields;
                answered = values.every(value => fields.every(field => !field.required || lang.isNotEmpty(value[field.name])));
            } else if(this.options?.length > 0) {
                // 選項有說明要求，例如 scq, mcq & ynq
                answered = lang.isNotEmpty(this.answer.elaboration) ||  this.options.filter(option => values.includes(option.value)).every(option => !option.elaboration?.required);
            } else {
                answered = true;
            }
        }
        return answered;
    }

    toString() {
        return `${this.description}:${this.answer}`
    }

    toJSON() {
        return ({
            type: this.type,
            required: this.required,
            condition: this.condition,
            description: this.description,
            helperText: this.helperText,
            options: this.options,
            answer: this.answer,
            format: this.format,
            questions: this.questions
        });
    }

    static fromJSON(json) {
        if(lang.isNullOrUndefined(json)) {
            return json;
        } else {
            return new Question(json);
        }
    }

    clone() {
        const json = this.toJSON();
        return Question.fromJSON(json);
    }

    static of(value) {
        let question;
        if(lang.isNullOrUndefined(value)) {
            question = value;
        } else if(value instanceof Question) {
            question = value.clone();
        } else if(lang.isObject(value)) {
            question = new Question(value);
        } else {
            throw new Error(`Type is not supported. Supported types are Question or JSON object, but receive: ${value}`);
        }
        return question;
    }
}

class Option {
    #config = {}
    #value = undefined
    #text = undefined
    #elaboration = undefined

    constructor(config) {
        if(lang.isPrimitive(config)) {
            this.#config = ({
                value: config
            });
        } else if(lang.isObject(config)) {
            this.#config = config;
        } else {
            throw new Error(`Type is not supported. Supported types are JSON object or formatted string("(required|optional)[:<type>]"), but receive: ${config}`);
        }
    }

    get value() {
        if(this.#value===undefined) {
            const {value = null} = this.#config;
            this.#value = value;
        }
        return this.#value;
    }

    get text() {
        if(this.#text===undefined) {
            const {text = null} = this.#config;
            this.#text = text;
        }
        return this.#text;
    }

    get elaboration() {
        if(this.#elaboration===undefined) {
            const {elaboration = null} = this.#config;
            this.#elaboration = Elaboration.of(elaboration)
        }
        return this.#elaboration;
    }

    toJSON() {
        if(this.elaboration===null&&this.text===null) {
            return this.value;
        } else {
            return ({
                value: this.value,
                text: this.text,
                elaboration: this.elaboration
            });
        }
    }

    static fromJSON(json) {
        if(lang.isNullOrUndefined(json)) {
            return json;
        } else {
            return new Option(json);
        }
    }

    clone() {
        const json = this.toJSON();
        return Option.fromJSON(json);
    }

    static of(value) {
        let option;
        if(lang.isNullOrUndefined(value)) {
            option = value;
        } else if(value instanceof Option) {
            option = value.clone();
        } else if(lang.isObject(value) || lang.isPrimitive(value)) {
            option = new Option(value);
        } else {
            throw new Error(`Type is not supported. Supported types are Option, JSON object or string, but receive: ${value}`);
        }
        return option;
    }
}

class Elaboration {
    #config = {}
    #description = undefined
    #required = undefined
    #type = undefined
    #multiple = undefined
    #min = undefined
    #max = undefined
    #step = undefined
    #maxlength = undefined
    #minlength = undefined
    #pattern = undefined
    #placeholder = undefined
    #helperText = undefined

    constructor(config) {
        if(lang.isString(config)) {
            const [description, rest = ''] = config.split(':');
            const options = rest.split(',').map(option => option.trim());
            const required = !options.includes('optional');
            const multiple = options.includes('multiple');
            const type = options.find(option => option!=='optional' && option!=='required' && option!=='multiple') || 'string';
            this.#config = ({description, required, type, multiple});
        } else if(lang.isObject(config)) {
            this.#config = config;
        } else {
            throw new Error(`Type is not supported. Supported types are JSON object or formatted string("<description>:<type>,(required)"), but receive: ${config}`);
        }
    }

    get description() {
        if(this.#description===undefined) {
            const {description = null} = this.#config;
            this.#description = description;
        }
        return this.#description;
    }

    get multiple() {
        if(this.#multiple===undefined) {
            const {multiple = false} = this.#config;
            this.#multiple = multiple;
        }
        return this.#multiple;
    }

    get required() {
        if(this.#required===undefined) {
            const {required = false} = this.#config;
            this.#required = required;
        }
        return this.#required;
    }

    get type() {
        if(this.#type===undefined) {
            const {type = 'string'} = this.#config;
            this.#type = type;
        }
        return this.#type;
    }

    get min() {
        if(this.#min===undefined) {
            const {min = null} = this.#config;
            this.#min = min;
        }
        return this.#min;
    }

    get max() {
        if(this.#max===undefined) {
            const {max = null} = this.#config;
            this.#max = max;
        }
        return this.#max;
    }

    get step() {
        if(this.#step===undefined) {
            const {step = null} = this.#config;
            this.#step = step;
        }
        return this.#step;
    }

    get maxlength() {
        if(this.#maxlength===undefined) {
            const {maxlength = null} = this.#config;
            this.#maxlength = maxlength;
        }
        return this.#maxlength;
    }

    get minlength() {
        if(this.#minlength===undefined) {
            const {minlength = null} = this.#config;
            this.#minlength = minlength;
        }
        return this.#minlength;
    }

    get pattern() {
        if(this.#pattern===undefined) {
            const {pattern = null} = this.#config;
            this.#pattern = pattern;
        }
        return this.#pattern;
    }

    get placeholder() {
        if(this.#placeholder===undefined) {
            const {placeholder = null} = this.#config;
            this.#placeholder = placeholder;
        }
        return this.#placeholder;
    }

    get helperText() {
        if(this.#helperText===undefined) {
            const {helperText = null} = this.#config;
            this.#helperText = helperText;
        }
        return this.#helperText;
    }

    toJSON() {
        let json;
        if(lang.isNull(this.min, this.max, this.step, this.maxlength, this.minlength, this.pattern)) {
            let options = (this.type!=='string') ? this.type : '';
            if(!this.required) {
                options = options + ',optional';
            }
            if(this.multiple) {
                options = options + ',multiple';
            }
            if(options.startsWith(',')) {
                options = options.substr(1);
            }

            json = this.description ? this.description : '';
            if(options.length>0) {
                json = json + `:${options}`;
            }
        } else {
            json = {
                description: this.description,
                type: this.type,
                required: this.required,
                multiple: this.multiple,
                min: this.min,
                max: this.max,
                step: this.step,
                maxlength: this.maxlength,
                minlength: this.minlength,
                pattern: this.pattern,
                placeholder: this.placeholder,
                helperText: this.helperText
            }
        }

        return json;
    }

    static fromJSON(json) {
        if(lang.isNullOrUndefined(json)) {
            return json;
        } else {
            return new Elaboration(json);
        }
    }

    clone() {
        const json = this.toJSON();
        return Elaboration.fromJSON(json);
    }

    static of(value) {
        let elaboration;
        if(lang.isNullOrUndefined(value)) {
            elaboration = value;
        } else if(value instanceof Elaboration) {
            elaboration = value.clone();
        } else if(lang.isObject(value) || lang.isString(value)) {
            elaboration = new Elaboration(value);
        } else {
            throw new Error(`Type is not supported. Supported types are Elaboration, JSON object or string, but receive: ${value}`);
        }
        return elaboration;
    }
}

class Format {
    #config = {}
    #fields = undefined
    #multiple = undefined
    #examples = undefined

    constructor(config) {
        this.#config = config;
    }

    get fields() {
        if(this.#fields===undefined) {
            const {fields = []} = this.#config;
            this.#fields = fields.map(field => Field.of(field));
        }
        return this.#fields;
    }

    get multiple() {
        if(this.#multiple===undefined) {
            const {multiple} = this.#config;
            this.#multiple = multiple;
        }
        return this.#multiple;
    }

    get examples() {
        if(this.#examples===undefined) {
            const {examples} = this.#config;
            this.#examples = examples;
        }
        return this.#examples;
    }

    toJSON() {
        return this.fields.length === 0 ? null : ({
            fields: this.fields,
            multiple: this.multiple,
            examples: this.examples
        });
    }

    static fromJSON(json) {
        if(lang.isNullOrUndefined(json)) {
            return json;
        } else {
            return new Format(json);
        }
    }

    clone() {
        const json = this.toJSON();
        return Format.fromJSON(json);
    }

    static of(value) {
        let format;
        if(lang.isNullOrUndefined(value)) {
            format = value;
        } else if(value instanceof Format) {
            format = value.clone();
        } else if(lang.isObject(value)) {
            format = new Format(value);
        } else {
            throw new Error(`Type is not supported. Supported types are Format or JSON object, but receive: ${value}`);
        }
        return format;
    }
}

class Field {
    #config = {}
    #name = undefined
    #required = undefined
    #type = undefined
    #multiple = undefined
    #min = undefined
    #max = undefined
    #step = undefined
    #maxlength = undefined
    #minlength = undefined
    #pattern = undefined
    #helperText = undefined
    #placeholder = undefined
    #flex = undefined

    constructor(config) {
        if(lang.isString(config)) {
            const [name, rest = ''] = config.split(':');
            const options = rest.split(',').map(option => option.trim());
            const required = !options.includes('optional');
            const multiple = options.includes('multiple');
            const type = options.find(option => option!=='required'&&option!=='optional'&&option!=='multiple') || 'text';
            this.#config = ({name, required, type, multiple});
        } else if(lang.isObject(config)) {
            this.#config = config;
        } else {
            throw new Error(`Type is not supported. Supported types are JSON object or formatted string("<name>:<type>,(required)"), but receive: ${config}`);
        }
    }

    get name() {
        if(this.#name===undefined) {
            const {name = null} = this.#config;
            this.#name = name;
        }
        return this.#name;
    }

    get required() {
        if(this.#required===undefined) {
            const {required = false} = this.#config;
            this.#required = required;
        }
        return this.#required;
    }

    get type() {
        if(this.#type===undefined) {
            const {type = 'text'} = this.#config;
            this.#type = type;
        }
        return this.#type;
    }

    get multiple() {
        if(this.#multiple===undefined) {
            const {multiple = false} = this.#config;
            this.#multiple = multiple;
        }
        return this.#multiple;
    }

    get min() {
        if(this.#min===undefined) {
            const {min = null} = this.#config;
            this.#min = min;
        }
        return this.#min;
    }

    get max() {
        if(this.#max===undefined) {
            const {max = null} = this.#config;
            this.#max = max;
        }
        return this.#max;
    }

    get step() {
        if(this.#step===undefined) {
            const {step = null} = this.#config;
            this.#step = step;
        }
        return this.#step;
    }

    get maxlength() {
        if(this.#maxlength===undefined) {
            const {maxlength = null} = this.#config;
            this.#maxlength = maxlength;
        }
        return this.#maxlength;
    }

    get minlength() {
        if(this.#minlength===undefined) {
            const {minlength = null} = this.#config;
            this.#minlength = minlength;
        }
        return this.#minlength;
    }

    get pattern() {
        if(this.#pattern===undefined) {
            const {pattern = null} = this.#config;
            this.#pattern = pattern;
        }
        return this.#pattern;
    }

    get placeholder() {
        if(this.#placeholder===undefined) {
            const {placeholder = null} = this.#config;
            this.#placeholder = placeholder;
        }
        return this.#placeholder;
    }

    get helperText() {
        if(this.#helperText===undefined) {
            const {helperText = null} = this.#config;
            this.#helperText = helperText;
        }
        return this.#helperText;
    }
    get flex() {
        if(this.#flex===undefined) {
            const {flex = null} = this.#config;
            this.#flex = flex;
        }
        return this.#flex;
    }

    toJSON() {
        let json;
        if(lang.isNull(this.min, this.max, this.step, this.maxlength, this.minlength, this.pattern, this.placeholder, this.helperText, this.flex)) {
            let options = (this.type!=='string') ? this.type : '';
            if(!this.required) {
                options = options + ',optional';
            }

            if(this.multiple) {
                options = options + ',multiple';
            }

            if(options.startsWith(',')) {
                options = options.substr(1);
            }

            json = this.name ? this.name : '';
            if(options.length>0) {
                json = json + `:${options}`;
            }
        } else {
            json = {
                name: this.name,
                type: this.type,
                required: this.required,
                multiple: this.multiple,
                min: this.min,
                max: this.max,
                step: this.step,
                maxlength: this.maxlength,
                minlength: this.minlength,
                pattern: this.pattern,
                placeholder: this.placeholder,
                helperText: this.helperText,
                flex: this.flex
            }
        }
        return json;
    }

    static fromJSON(json) {
        if(lang.isNullOrUndefined(json)) {
            return json;
        } else {
            return new Field(json);
        }
    }

    clone() {
        const json = this.toJSON();
        return Field.fromJSON(json);
    }

    static of(value) {
        let field;
        if(lang.isNullOrUndefined(value)) {
            field = value;
        } else if(value instanceof Field) {
            field = value.clone();
        } else if(lang.isObject(value) || lang.isString(value)) {
            field = new Field(value);
        } else {
            throw new Error(`Type is not supported. Supported types are Field, JSON object or string, but receive: ${value}`);
        }
        return field;
    }
}

class Answer {
    #config = {}
    #value = undefined
    #elaboration = undefined

    constructor(config) {
        if(lang.isObject(config) && lang.has(config, 'value')) {
            this.#config = config;
        } else {
            this.#config = ({
                value: config
            });
        }
    }

    get value() {
        if(this.#value===undefined) {
            const {value} = this.#config;
            this.#value = value;
        }
        return this.#value;
    }

    set value(value) {
        this.#value = value;
    }

    get elaboration() {
        if(this.#elaboration===undefined) {
            const {elaboration} = this.#config;
            this.#elaboration = elaboration;
        }
        return this.#elaboration;
    }

    set elaboration(elaboration) {
        this.#elaboration = elaboration;
    }

    equals(another) {
        if(!another) return false;
        return this.value === another.value && this.elaboration === another.elaboration;
    }

    toString() {
        let s = lang.isNullOrUndefined(this.value) ? '' : this.value.toString();
        if(lang.isNotEmpty(this.elaboration)) {
            s = s + `(${this.elaboration})`
        }
        return s;
    }

    toJSON() {
        if(lang.isEmpty(this.elaboration)) {
            if(lang.isEmpty(this.value)) {
                return null;
            } else {
                return this.value;
            }
        } else {
            return ({
                value: this.value,
                elaboration: this.elaboration
            });
        }
    }

    static fromJSON(json) {
        if(lang.isNullOrUndefined(json)) {
            return json;
        } else {
            return new Answer(json);
        }
    }

    clone() {
        const json = this.toJSON();
        return Answer.fromJSON(json);
    }

    static of(value) {
        let answer;
        if(lang.isNullOrUndefined(value)) {
            answer = value;
        } else if(value instanceof Answer) {
            answer = value.clone();
        } else if(lang.isObject(value) || lang.isPrimitive(value) || lang.isArray(value)) {
            answer = new Answer(value);
        } else {
            throw new Error(`Type is not supported. Supported types are Answer, JSON object, array or primitive, but receive: ${value}`);
        }

        return answer;
    }
}


Question.Type = {
    MCQ: 'mcq',
    SCQ: 'scq',
    OEQ: 'oeq',
    YNQ: 'ynq'
}


Questionnaire.Section = Section;
Questionnaire.Question = Question;

export default Questionnaire;

export { Section, Answer, Question };